import { useState } from "react";
import { Loader2, Mail, Phone, MapPin, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { PageHero, Section, Seo, Placeholderize, breadcrumb } from "@/components/site/Section";
import { http, errorMessage } from "@/lib/api";

const validate = (f) => {
  const e = {};
  if (f.name.trim().length < 2) e.name = "Please enter your name.";
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(f.email)) e.email = "Please enter a valid email address.";
  if (f.message.trim().length < 10) e.message = "Please write a message of at least 10 characters.";
  return e;
};

const Field = ({ id, label, error, children }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-sm text-[#F7F5F0]/80">{label}</Label>
    {children}
    {error && <p id={`${id}-error`} className="text-sm text-[#ff8a3d]" data-testid={`${id}-error`}>{error}</p>}
  </div>
);

const DETAILS = [
  { icon: Mail, t: "Email", v: "hello@weelogrun.co.uk", href: "mailto:hello@weelogrun.co.uk" },
  { icon: Phone, t: "Telephone", v: "07756 124 224", href: "tel:+447756124224" },
  { icon: MapPin, t: "Address", v: "32 Kingsmoss Road, Newtownabbey BT36 4TN" },
];

export default function Contact() {
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "" });
  const [errors, setErrors] = useState({});
  const [state, setState] = useState({ loading: false, done: "", error: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const v = validate(f);
    setErrors(v);
    if (Object.keys(v).length) return;
    setState({ loading: true, done: "", error: "" });
    try {
      const { data } = await http.post("/contact", { ...f, phone: f.phone || null });
      setState({ loading: false, done: data.message, error: "" });
    } catch (err) {
      setState({ loading: false, done: "", error: errorMessage(err) });
    }
  };

  const inp = (k, extra = {}) => ({
    id: `contact-${k}`, value: f[k], onChange: set(k), "aria-invalid": !!errors[k],
    "aria-describedby": errors[k] ? `contact-${k}-error` : undefined, "data-testid": `contact-${k}-input`, ...extra,
  });

  return (
    <>
      <Seo title="Contact Us — Newtownabbey Log Delivery" path="/contact" jsonLd={breadcrumb("Contact", "/contact")} description="Contact WeeLogRun at 32 Kingsmoss Road, Newtownabbey BT36 4TN. Call 07756 124 224 or email hello@weelogrun.co.uk about firewood deliveries." />
      <PageHero eyebrow="Contact" title="We're here to help." intro="Questions about plans, deliveries or your subscription? Send us a message and we'll get back to you." />
      <Section className="!pt-14">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            {state.done ? (
              <div className="flex gap-4 rounded-3xl border border-[#FF6A00]/40 bg-[#FF6A00]/10 p-8" data-testid="contact-success">
                <CheckCircle2 className="h-7 w-7 shrink-0 text-[#FF6A00]" />
                <p className="text-lg">{state.done}</p>
              </div>
            ) : (
              <form onSubmit={submit} noValidate className="space-y-5 rounded-3xl border border-[#262626] bg-[#101010] p-6 sm:p-8" data-testid="contact-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field id="contact-name" label="Full name" error={errors.name}><Input {...inp("name", { autoComplete: "name" })} className="lc-input" /></Field>
                  <Field id="contact-email" label="Email address" error={errors.email}><Input {...inp("email", { type: "email", autoComplete: "email" })} className="lc-input" /></Field>
                </div>
                <Field id="contact-phone" label="Telephone (optional)"><Input {...inp("phone", { type: "tel", autoComplete: "tel" })} className="lc-input" /></Field>
                <Field id="contact-message" label="Your message" error={errors.message}><Textarea {...inp("message")} rows={6} className="rounded-xl border-[#2E2E2E] bg-[#0f0f0f] text-base" /></Field>
                {state.error && <p className="text-sm text-[#ff8a3d]" role="alert" data-testid="contact-error">{state.error}</p>}
                <button type="submit" disabled={state.loading} className="lc-btn-primary w-full sm:w-auto" data-testid="contact-submit">
                  {state.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send message"}
                </button>
              </form>
            )}
          </div>
          <div className="space-y-4 lg:col-span-5">
            {DETAILS.map(({ icon: Icon, t, v, href }) => (
              <div key={t} className="flex gap-4 rounded-2xl border border-[#262626] p-5">
                <Icon className="h-6 w-6 shrink-0 text-[#D4A437]" />
                <div>
                  <p className="text-sm font-semibold">{t}</p>
                  <p className="mt-1 text-sm text-[#A1A1AA]">{href ? <a href={href} className="text-[#FF6A00] hover:underline" data-testid={`contact-${t.toLowerCase()}-link`}>{v}</a> : <Placeholderize text={v} />}</p>
                </div>
              </div>
            ))}
            <p className="px-1 text-sm text-[#A1A1AA]">Existing members can also manage their subscription any time from My WeeLogRun.</p>
          </div>
        </div>
      </Section>
    </>
  );
}
